// pages/EventTracking.tsx
import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Input,
  Select,
  Button,
  Table,
  Space,
  Badge,
  Progress,
  Tooltip,
  DatePicker,
} from "antd";
import type { Dayjs } from "dayjs";
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  LineChartOutlined,
  TeamOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import Layout from "../../components/layout/Layout";
import { useGetEventTracking } from "../../hooks/api/notification/notificationService";
import { ClipLoader } from "react-spinners";
import LoadingState from "../../components/common/LoadingState";
import NoData from "../../components/common/NoData";

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const EventTracking = () => {
  const [params, setParams] = useState({
    startDate: "",
    endDate: "",
    eventType: "all",
    page: 1,
    limit: 15,
    search: "",
    interval:""
  });

  const { data: eventsResponse, isLoading, isError, error } = useGetEventTracking(params);

  const eventStats = eventsResponse?.data?.eventStats;
  const trackedEventTypes = eventsResponse?.data?.trackedEventTypes || [];
  const liveEvents = eventsResponse?.data?.liveEvents || [];
  const pagination = eventsResponse?.data?.pagination;
  const filters = eventsResponse?.data?.filters;
  const timeRangeInfo = eventsResponse?.data?.timeRangeInfo;

  const handleSearch = (value: string) => {
    setParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (field: string, value: string) => {
    setParams((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setParams((prev) => ({
        ...prev,
        startDate: dates[0]!.format("YYYY-MM-DD"),
        endDate: dates[1]!.format("YYYY-MM-DD"),
        interval: "custom",
        page: 1,
      }));
    } else {
      setParams((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
        interval: "",
        page: 1,
      }));
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setParams((prev) => ({ ...prev, page, limit: pageSize }));
  };

  const getEventTypeColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      "New Sub": "green",
      UnSub: "red",
      "Get Profile": "blue",
      visitor_load: "cyan",
      login_success: "green",
      login_fail: "red",
      session_start: "purple",
      session_end: "gray",
      level_completed: "orange",
      leaderboard_update: "gold",
      subscription_start: "purple",
      subscription_expiry: "volcano",
      subscription_renewal: "magenta",
      wallet_topup: "lime",
      wallet_low_balance: "red",
    };
    return colorMap[type] || "default";
  };

  const getTrendIcon = (trend: string, change: number) => {
    if (trend === "up" || change > 0) {
      return <ArrowUpOutlined style={{ color: "#52c41a" }} />;
    } else if (trend === "down" || change < 0) {
      return <ArrowDownOutlined style={{ color: "#ff4d4f" }} />;
    }
    return null;
  };

  const getTrendColor = (trend: string, change: number) => {
    if (trend === "up" || change > 0) return "#52c41a";
    if (trend === "down" || change < 0) return "#ff4d4f";
    return "#faad14";
  };

  const formatChange = (change: number) => {
    if (change > 0) return `+${change.toFixed(1)}%`;
    if (change < 0) return `${change.toFixed(1)}%`;
    return "0%";
  };

  const statsColumns = [
    {
      title: "Event Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={getEventTypeColor(type)}>{type}</Tag>
      ),
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      render: (count: number, record: any) => (
        <Space>
          <span className="font-semibold">{count}</span>
          {record.previousCount && (
            <span className="text-xs text-gray-500">
              (prev: {record.previousCount})
            </span>
          )}
        </Space>
      ),
    },
    {
      title: "Unique Users",
      dataIndex: "uniqueUsers",
      key: "uniqueUsers",
      render: (uniqueUsers: number, record: any) => (
        <Space>
          <span>{uniqueUsers}</span>
          {record.previousUniqueUsers && (
            <span className="text-xs text-gray-500">
              (prev: {record.previousUniqueUsers})
            </span>
          )}
        </Space>
      ),
    },
    {
      title: "Change",
      key: "change",
      render: (record: any) => (
        <Space>
          {getTrendIcon(record.trend, record.countChange)}
          <span
            style={{ color: getTrendColor(record.trend, record.countChange) }}
          >
            {formatChange(record.countChange)}
          </span>
        </Space>
      ),
    },
  ];

  const eventColumns = [
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      render: (eventType: string) => (
        <Tag color={getEventTypeColor(eventType)}>{eventType}</Tag>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "userId",
      key: "userId",
      // render: (userId: string) => (
      //   <Badge status="" text={userId} />
      // ),
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: string) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        return (
          <div>
            <div className="font-medium">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="text-xs text-gray-500">
              {date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>
          </div>
        );
      },
    },
    // {
    //   title: 'Metadata',
    //   dataIndex: 'metadata',
    //   key: 'metadata',
    //   ellipsis: true,
    //   width: 200,
    // },
  ];

  console.log("🚀 ~ file: EventTracking.tsx:106 ~ EventTracking ~ data:", params)

  return (
    <Layout
      pageName="Event Tracking"
      icon={<FilterOutlined />}
      metricsTitle="Real-time Event Monitoring"
      showMetrics={false}
      showFilter2={false}
      showTimeLine={false}
      extraChildren={
        <div className="flex gap-4">
          <Select
            value={params.eventType}
            onChange={(value) => handleFilterChange("eventType", value)}
            style={{ width: 200 }}
            size="large"
          >
            <Option value="all">All Events</Option>
            {trackedEventTypes.map((eventType) => (
              <Option key={eventType.type} value={eventType.type}>
                {eventType.type}
              </Option>
            ))}
          </Select>
          <RangePicker
            onChange={handleDateRangeChange}
            size="large"
            style={{ width: 300 }}
            placeholder={["Start Date", "End Date"]}
            format="YYYY-MM-DD"
          />
        </div>
      }
      // extraChildren={
      //   <Button className="whitespace-nowrap bg-primary text-white flex items-center gap-2 px-4 py-6 rounded-md hover:!bg-primary hover:!text-white" icon={<DownloadOutlined />}>
      //     Export Events
      //   </Button>
      // }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <div className="flex flex-col justify-center items-center h-[60vh]">
          <div className="text-center space-y-4">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-800">
              Error Loading Events
            </h3>
            <p className="text-gray-600">
              {error?.message || "Failed to load event tracking data. Please try again."}
            </p>
            <Button
              type="primary"
              onClick={() => window.location.reload()}
              className="mt-4"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : !eventsResponse?.data || (!trackedEventTypes.length && !liveEvents.length && !eventStats) ? (
        <NoData 
          header="No Event Tracking Data Available" 
          paragraph="There are no events to display for the selected time range and filters."
        />
      ) : (
        <div className="mt-6 space-y-6">
          {/* Event Statistics Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Events"
                  value={eventStats?.totalEvents || 0}
                  prefix={<LineChartOutlined />}
                  valueStyle={{ color: "#3f8600" }}
                  suffix={
                    eventStats?.eventChange !== undefined && (
                      <span
                        style={{
                          color:
                            eventStats.eventChange > 0
                              ? "#52c41a"
                              : eventStats.eventChange < 0
                              ? "#ff4d4f"
                              : "#faad14",
                          fontSize: "12px",
                        }}
                      >
                        {formatChange(eventStats.eventChange)}
                      </span>
                    )
                  }
                />
                <div className="text-xs text-gray-500 mt-2">
                  {eventStats?.timeRange || "Last hour"}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Unique Users"
                  value={eventStats?.uniqueUsers || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                  suffix={
                    eventStats?.userChange !== undefined && (
                      <span
                        style={{
                          color:
                            eventStats.userChange > 0
                              ? "#52c41a"
                              : eventStats.userChange < 0
                              ? "#ff4d4f"
                              : "#faad14",
                          fontSize: "12px",
                        }}
                      >
                        {formatChange(eventStats.userChange)}
                      </span>
                    )
                  }
                />
                <div className="text-xs text-gray-500 mt-2">
                  Active in period
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Latest Event"
                  value={
                    eventStats?.latestEvent
                      ? new Date(eventStats.latestEvent).toLocaleTimeString()
                      : "No events"
                  }
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
                <div className="text-xs text-gray-500 mt-2">
                  {eventStats?.latestEvent
                    ? new Date(eventStats.latestEvent).toLocaleDateString()
                    : ""}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <div className="text-sm text-gray-500 mb-2">
                  Previous Period
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Events:</span>
                    <span>{eventStats?.previousPeriod?.totalEvents || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Users:</span>
                    <span>{eventStats?.previousPeriod?.uniqueUsers || 0}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {timeRangeInfo?.label
                    ? `Compared to previous ${timeRangeInfo.label.toLowerCase()}`
                    : "Previous period"}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Tracked Event Types */}
          <Card
            title="Event Type Analytics"
            extra={
              <span className="text-gray-500 text-sm">
                {timeRangeInfo?.label || "Current period"}
              </span>
            }
          >
            <Table
              columns={statsColumns}
              dataSource={trackedEventTypes}
              rowKey="type"
              pagination={false}
              size="small"
              loading={isLoading}
            />
          </Card>

          {/* Event Type Distribution Visualization */}
          {trackedEventTypes.length > 0 && (
            <Card title="Event Distribution">
              <Row gutter={[16, 16]}>
                {trackedEventTypes.map((eventType) => (
                  <Col xs={24} md={8} key={eventType.type}>
                    <Card size="small">
                      <div className="flex justify-between items-center mb-2">
                        <Tag color={getEventTypeColor(eventType.type)}>
                          {eventType.type}
                        </Tag>
                        <Space>
                          {getTrendIcon(eventType.trend, eventType.countChange)}
                          <span
                            style={{
                              color: getTrendColor(
                                eventType.trend,
                                eventType.countChange
                              ),
                              fontSize: "12px",
                            }}
                          >
                            {formatChange(eventType.countChange)}
                          </span>
                        </Space>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Events:</span>
                          <span className="font-semibold">
                            {eventType.count}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Unique Users:</span>
                          <span>{eventType.uniqueUsers}</span>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* Filters */}
          <Card>
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="flex-1">
                <Search
                  placeholder="Search events by phone number or type..."
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  onSearch={handleSearch}
                  style={{ width: "100%" }}
                  className=""
                />
              </div>
            </div>
          </Card>

          {/* Live Events Stream */}
          <Card
            title="Live Event Stream"
            extra={
              <span className="text-gray-500 text-sm">
                {timeRangeInfo?.label
                  ? `Events from ${timeRangeInfo.label.toLowerCase()}`
                  : "Recent events"}
              </span>
            }
          >
            <Table
              columns={eventColumns}
              dataSource={liveEvents}
              rowKey={(record) => `${record.userId}-${record.timestamp}`}
              loading={isLoading}
              pagination={{
                current: params.page,
                pageSize: params.limit,
                total: pagination?.total || 0,
                onChange: handlePageChange,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default EventTracking;
